import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { ArrowLeft, Plus, Trash2, Copy, ChevronDown, ChevronUp, Save, Pencil, Settings, Lock, Clock } from "lucide-react";
import BlockEditor, { BLOCK_TYPES } from "../../components/BlockEditor";
import RichTextEditor from "../../components/RichTextEditor";
import ImageUpload from "../../components/ImageUpload";

const makeId = (prefix = "id") => `${prefix}_${Math.random().toString(36).slice(2, 10)}`;

const defaultQuizQuestion = () => ({
  id: makeId("q"),
  text: "",
  type: "single",
  answers: [
    { id: makeId("a"), text: "", isCorrect: true },
    { id: makeId("a"), text: "", isCorrect: false },
  ],
});

const defaultQuiz = () => ({
  passingScore: 80,
  pagination: true,
  questions: [defaultQuizQuestion()],
});

const normalizeQuiz = (quiz) => {
  const base = quiz && typeof quiz === "object" ? quiz : {};
  const questions = Array.isArray(base.questions) && base.questions.length > 0 ? base.questions : defaultQuiz().questions;

  return {
    passingScore: Number.isFinite(Number(base.passingScore)) ? Number(base.passingScore) : 80,
    pagination: base.pagination !== false,
    questions: questions.map((question) => ({
      id: question.id || makeId("q"),
      text: question.text || question.question || "",
      type: question.type === "multiple" ? "multiple" : "single",
      answers: (Array.isArray(question.answers) && question.answers.length > 0 ? question.answers : defaultQuizQuestion().answers).map((answer) => ({
        id: answer.id || makeId("a"),
        text: answer.text || "",
        isCorrect: Boolean(answer.isCorrect),
      })),
    })),
  };
};

const defaultAssignmentSettings = (settings = {}) => ({
  enable_rating:
    settings.enable_rating !== undefined
      ? Boolean(settings.enable_rating)
      : true,

  enable_private_discussion:
    settings.enable_private_discussion !== undefined
      ? Boolean(settings.enable_private_discussion)
      : true,

  allow_multiple_files:
    settings.allow_multiple_files !== undefined
      ? Boolean(settings.allow_multiple_files)
      : true,

  enable_round_trips:
    settings.enable_round_trips !== undefined
      ? Boolean(settings.enable_round_trips)
      : true,
});

export default function AdminCourseEditor() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [allCourses, setAllCourses] = useState([]);
  const [activeLesson, setActiveLesson] = useState(null);
  const [activeLessonData, setActiveLessonData] = useState(null);
  const [savingMsg, setSavingMsg] = useState("");
  const [showCourseSettings, setShowCourseSettings] = useState(false);
  const [newModuleTitle, setNewModuleTitle] = useState("");

  const load = () => api.get(`/courses/${courseId}`).then((r) => setCourse(r.data));
  useEffect(() => {
    load();
    api.get("/courses/admin/all").then((r) => setAllCourses(r.data)).catch(() => {});
  }, [courseId]);  // eslint-disable-line react-hooks/exhaustive-deps

  const addModule = async () => {
    if (!newModuleTitle.trim()) return;
    const modules = [...(course.modules || []), { module_id: `mod_${Math.random().toString(36).slice(2, 12)}`, title: newModuleTitle, lessons: [] }];
    await api.patch(`/courses/${courseId}`, { modules });
    setNewModuleTitle("");
    load();
  };

  const renameModule = async (modId, title) => {
    const modules = course.modules.map((m) => m.module_id === modId ? { ...m, title } : m);
    await api.patch(`/courses/${courseId}`, { modules });
    load();
  };

  const removeModule = async (modId) => {
    if (!window.confirm("Supprimer ce module et toutes ses leçons ?")) return;
    const modules = course.modules.filter((m) => m.module_id !== modId);
    await api.patch(`/courses/${courseId}`, { modules });
    load();
  };

  const addLesson = async (modId, type = "lesson") => {
    const title = window.prompt(type === "assignment" ? "Titre du devoir :" : type === "quiz" ? "Titre du quiz :" : "Titre de la leçon :");
    if (!title) return;
    await api.post("/lessons", {
      course_id: courseId,
      module_id: modId,
      title,
      type,
      blocks: [],
      quiz: type === "quiz" ? defaultQuiz() : null,
      assignment_settings: type === "assignment" ? defaultAssignmentSettings() : null,
    });
    load();
  };

  const removeLesson = async (lessonId) => {
    if (!window.confirm("Supprimer cette leçon ?")) return;
    await api.delete(`/lessons/${lessonId}`);
    if (activeLesson === lessonId) { setActiveLesson(null); setActiveLessonData(null); }
    await load();
    window.location.reload();
  };

  const duplicateLesson = async (lessonId) => {
    await api.post(`/lessons/${lessonId}/duplicate`);
    await load();
  };

  const openLesson = async (lessonId) => {
    setActiveLesson(lessonId);
    const { data } = await api.get(`/lessons/${lessonId}`);
    setActiveLessonData(
      data.type === "quiz"
        ? { ...data, quiz: normalizeQuiz(data.quiz) }
        : data.type === "assignment"
          ? { ...data, assignment_settings: defaultAssignmentSettings(data.assignment_settings) }
          : data
    );
  };

  const saveLesson = async () => {
    setSavingMsg("Sauvegarde...");
    await api.patch(`/lessons/${activeLesson}`, {
      title: activeLessonData.title,
      type: activeLessonData.type,
      blocks: activeLessonData.blocks,
      quiz: activeLessonData.type === "quiz" ? normalizeQuiz(activeLessonData.quiz) : null,
      assignment_settings: activeLessonData.type === "assignment" ? defaultAssignmentSettings(activeLessonData.assignment_settings) : null,
    });
    setSavingMsg("Enregistré ✓");
    setTimeout(() => setSavingMsg(""), 2000);
    load();
  };

  const addBlock = (type) => {
    const block = { id: `blk_${Math.random().toString(36).slice(2, 12)}`, type, content: {} };
    setActiveLessonData((d) => ({ ...d, blocks: [...(d.blocks || []), block] }));
  };

  const updateBlock = (idx, block) => {
    setActiveLessonData((d) => {
      const blocks = [...d.blocks];
      blocks[idx] = block;
      return { ...d, blocks };
    });
  };
  const removeBlock = (idx) => setActiveLessonData((d) => ({ ...d, blocks: d.blocks.filter((_, i) => i !== idx) }));
  const moveBlock = (idx, dir) => setActiveLessonData((d) => {
    const blocks = [...d.blocks];
    const j = idx + dir;
    if (j < 0 || j >= blocks.length) return d;
    [blocks[idx], blocks[j]] = [blocks[j], blocks[idx]];
    return { ...d, blocks };
  });

  const updateCourseField = async (patch) => {
    await api.patch(`/courses/${courseId}`, patch);
    load();
  };

  const moveLesson = async (lessonId, targetModuleId, targetIndex = null) => {
    await api.patch(`/lessons/${lessonId}/move`, {
      target_module_id: targetModuleId,
      target_index: targetIndex,
    });

    setCourse((current) => {
      if (!current) return current;

      let movedLesson = null;
      const modules = (current.modules || []).map((module) => {
        const lessons = module.lessons || [];
        const found = lessons.find((lesson) => lesson.lesson_id === lessonId);
        if (found) movedLesson = found;
        return { ...module, lessons: lessons.filter((lesson) => lesson.lesson_id !== lessonId) };
      });

      if (!movedLesson) return current;

      const normalizedModules = modules.map((module) => {
        if (module.module_id !== targetModuleId) return module;
        const lessons = [...(module.lessons || [])];
        const safeIndex = targetIndex === null || targetIndex === undefined
          ? lessons.length
          : Math.max(0, Math.min(targetIndex, lessons.length));
        lessons.splice(safeIndex, 0, movedLesson);
        return { ...module, lessons };
      });

      return { ...current, modules: normalizedModules };
    });

    if (activeLesson === lessonId) {
      setActiveLessonData((current) => current ? { ...current, module_id: targetModuleId } : current);
    }

    load();
  };

  const moveLessonWithinModule = (module, lessonIndex, direction) => {
    const lessons = module.lessons || [];
    const nextIndex = lessonIndex + direction;
    if (nextIndex < 0 || nextIndex >= lessons.length) return;
    moveLesson(lessons[lessonIndex].lesson_id, module.module_id, nextIndex);
  };

  const moveLessonToModule = (lessonId, targetModuleId) => {
    const targetModule = (course.modules || []).find((module) => module.module_id === targetModuleId);
    moveLesson(lessonId, targetModuleId, targetModule?.lessons?.length || 0);
  };


  const updateQuiz = (patch) => {
    setActiveLessonData((current) => ({
      ...current,
      quiz: { ...normalizeQuiz(current.quiz), ...patch },
    }));
  };

  const updateQuizQuestion = (questionIndex, patch) => {
    setActiveLessonData((current) => {
      const quiz = normalizeQuiz(current.quiz);
      const questions = [...quiz.questions];
      questions[questionIndex] = { ...questions[questionIndex], ...patch };

      if (patch.type === "single") {
        const firstCorrect = questions[questionIndex].answers.findIndex((answer) => answer.isCorrect);
        questions[questionIndex].answers = questions[questionIndex].answers.map((answer, index) => ({
          ...answer,
          isCorrect: index === (firstCorrect >= 0 ? firstCorrect : 0),
        }));
      }

      return { ...current, quiz: { ...quiz, questions } };
    });
  };

  const updateQuizAnswer = (questionIndex, answerIndex, patch) => {
    setActiveLessonData((current) => {
      const quiz = normalizeQuiz(current.quiz);
      const questions = [...quiz.questions];
      const question = { ...questions[questionIndex] };
      const answers = [...question.answers];

      if (question.type === "single" && patch.isCorrect === true) {
        question.answers = answers.map((answer, index) => ({ ...answer, isCorrect: index === answerIndex }));
      } else {
        answers[answerIndex] = { ...answers[answerIndex], ...patch };
        question.answers = answers;
      }

      questions[questionIndex] = question;
      return { ...current, quiz: { ...quiz, questions } };
    });
  };

  const addQuizQuestion = () => {
    setActiveLessonData((current) => {
      const quiz = normalizeQuiz(current.quiz);
      return { ...current, quiz: { ...quiz, questions: [...quiz.questions, defaultQuizQuestion()] } };
    });
  };

  const removeQuizQuestion = (questionIndex) => {
    setActiveLessonData((current) => {
      const quiz = normalizeQuiz(current.quiz);
      const questions = quiz.questions.filter((_, index) => index !== questionIndex);
      return { ...current, quiz: { ...quiz, questions: questions.length ? questions : [defaultQuizQuestion()] } };
    });
  };

  const addQuizAnswer = (questionIndex) => {
    setActiveLessonData((current) => {
      const quiz = normalizeQuiz(current.quiz);
      const questions = [...quiz.questions];
      const question = { ...questions[questionIndex] };
      question.answers = [...question.answers, { id: makeId("a"), text: "", isCorrect: false }];
      questions[questionIndex] = question;
      return { ...current, quiz: { ...quiz, questions } };
    });
  };

  const updateAssignmentSettings = (patch) => {
    setActiveLessonData((current) => ({
      ...current,
      assignment_settings: { ...defaultAssignmentSettings(current.assignment_settings), ...patch },
    }));
  };

  const removeQuizAnswer = (questionIndex, answerIndex) => {
    setActiveLessonData((current) => {
      const quiz = normalizeQuiz(current.quiz);
      const questions = [...quiz.questions];
      const question = { ...questions[questionIndex] };
      const filteredAnswers = question.answers.filter((_, index) => index !== answerIndex);
      question.answers = filteredAnswers.length >= 2 ? filteredAnswers : question.answers;

      if (question.type === "single" && !question.answers.some((answer) => answer.isCorrect)) {
        question.answers = question.answers.map((answer, index) => ({ ...answer, isCorrect: index === 0 }));
      }

      questions[questionIndex] = question;
      return { ...current, quiz: { ...quiz, questions } };
    });
  };

  if (!course) return <div className="p-12">Chargement...</div>;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[420px_1fr] gap-0 min-h-[calc(100vh-76px)]" data-testid="admin-course-editor">
      <aside className="bg-white border-r border-emerald-100 p-4 lg:overflow-y-auto lg:max-h-[calc(100vh-76px)] lg:sticky lg:top-[76px]">
        <Link to="/admin/courses" className="text-sm text-gray-500 inline-flex items-center gap-1 mb-3 hover:text-brand-green"><ArrowLeft className="w-4 h-4" /> Retour</Link>
        <div className="flex items-center justify-between mb-1">
          <p className="font-heading font-bold text-sm line-clamp-2">{course.title}</p>
          <button onClick={() => setShowCourseSettings(!showCourseSettings)} className="p-1 text-gray-500 hover:text-brand-green" data-testid="course-settings-btn"><Settings className="w-4 h-4" /></button>
        </div>
        <p className="text-xs text-gray-500 mb-2">{course.price_eur}€ · {(course.modules || []).length} modules</p>
        <a href={`/formations/${course.slug || course.course_id}`} target="_blank" rel="noreferrer" className="text-xs text-brand-green hover:underline inline-flex items-center gap-1 mb-4">
          Voir la page publique : /formations/{course.slug || course.course_id}
        </a>

        {showCourseSettings && (
          <div className="card p-3 mb-4 space-y-2 text-sm" data-testid="course-settings-panel">
            <input className="input !text-sm !py-1.5" defaultValue={course.title} onBlur={(e) => updateCourseField({ title: e.target.value })} placeholder="Titre" />
            <div className="rounded-xl border border-emerald-100 bg-white p-3 space-y-2">
              <p className="text-xs font-bold uppercase tracking-wide text-brand-orange">SEO Google</p>
              <label className="block">
                <span className="text-xs font-semibold text-gray-700">URL SEO de la formation</span>
                <input
                  className="input !text-sm !py-1.5 mt-1 font-mono"
                  value={course.slug || ""}
                  onChange={(e) => setCourse((c) => ({ ...c, slug: e.target.value.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9-\s]/g, "").replace(/[\s_]+/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "") }))}
                  onBlur={(e) => updateCourseField({ slug: e.target.value })}
                  placeholder="ex : accompagner-entrepreneurs-recruter"
                  data-testid="course-seo-slug-input"
                />
                <span className="text-[11px] text-gray-500">Page publique : /formations/{course.slug || course.course_id}</span>
              </label>
              <label className="block">
                <span className="text-xs font-semibold text-gray-700">Sous-titre SEO ({(course.seo_subtitle || "").length}/120)</span>
                <input className="input !text-sm !py-1.5 mt-1" value={course.seo_subtitle || ""} onChange={(e) => setCourse((c) => ({ ...c, seo_subtitle: e.target.value }))} onBlur={(e) => updateCourseField({ seo_subtitle: e.target.value })} placeholder="Angle clair orienté recherche Google" data-testid="course-seo-subtitle-input" />
                <span className="text-[11px] text-gray-500">Ex : Programme pour reprendre confiance et sortir des blocages émotionnels.</span>
              </label>
              <label className="block">
                <span className="text-xs font-semibold text-gray-700">Titre SEO ({(course.seo_title || course.title || "").length}/60)</span>
                <input className="input !text-sm !py-1.5 mt-1" value={course.seo_title || ""} onChange={(e) => setCourse((c) => ({ ...c, seo_title: e.target.value }))} onBlur={(e) => updateCourseField({ seo_title: e.target.value })} placeholder="Titre affiché dans Google" data-testid="course-seo-title-input" />
              </label>
              <label className="block">
                <span className="text-xs font-semibold text-gray-700">Description SEO ({(course.seo_description || course.seo_subtitle || "").length}/160)</span>
                <textarea className="input !text-sm !py-1.5 mt-1" rows={2} value={course.seo_description || ""} onChange={(e) => setCourse((c) => ({ ...c, seo_description: e.target.value }))} onBlur={(e) => updateCourseField({ seo_description: e.target.value })} placeholder="Description courte optimisée Google" data-testid="course-seo-description-input" />
              </label>
            </div>
            <RichTextEditor value={course.short_description || ""} onChange={(value) => updateCourseField({ short_description: value })} placeholder="Description courte" minHeight={120} />
            <RichTextEditor value={course.long_description || ""} onChange={(value) => updateCourseField({ long_description: value })} placeholder="Description longue" minHeight={160} />
            <input className="input !text-sm !py-1.5" type="number" step="0.01" defaultValue={course.price_eur} onBlur={(e) => updateCourseField({ price_eur: parseFloat(e.target.value) })} placeholder="Prix" />
            <input className="input !text-sm !py-1.5" type="number" step="0.01" defaultValue={course.affiliate_commission_pct} onBlur={(e) => updateCourseField({ affiliate_commission_pct: parseFloat(e.target.value) })} placeholder="Commission % affilié" />
            <div className="rounded-xl border border-emerald-100 bg-emerald-50/60 p-3 space-y-3">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-1 h-5 w-5 accent-brand-green"
                  checked={!!course.drip_enabled}
                  onChange={(e) => updateCourseField({ drip_enabled: e.target.checked })}
                  data-testid="course-drip-enabled-toggle"
                />
                <span>
                  <span className="font-semibold flex items-center gap-2"><Lock className="w-4 h-4 text-brand-orange" /> Déblocage progressif hebdomadaire</span>
                  <span className="block text-xs text-gray-600 mt-1">OFF : tous les modules sont accessibles immédiatement. ON : semaine 1 immédiate, puis une nouvelle semaine selon le délai choisi.</span>
                </span>
              </label>
              <label className="block">
                <span className="text-xs font-semibold text-gray-700 flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> Délai entre deux modules (jours)</span>
                <input
                  className="input !text-sm !py-1.5 mt-1"
                  type="number"
                  min="1"
                  max="365"
                  value={course.drip_interval_days || 7}
                  disabled={!course.drip_enabled}
                  onChange={(e) => setCourse((c) => ({ ...c, drip_interval_days: e.target.value }))}
                  onBlur={(e) => updateCourseField({ drip_interval_days: parseInt(e.target.value || "7", 10) })}
                  data-testid="course-drip-interval-input"
                />
              </label>
            </div>
            <ImageUpload
              label="Image de présentation"
              type="image"
              storage="upload"
              uploadContext="course"
              seoSlug={course.slug || ""}
              seoTitle={course.title || ""}
              value={course.cover_image || ""}
              onChange={(url) => updateCourseField({ cover_image: url })}
              showCurrentUrl
              currentUrlLabel="Image actuelle"
            />
            <input className="input !text-sm !py-1.5" defaultValue={course.category || ""} onBlur={(e) => updateCourseField({ category: e.target.value })} placeholder="Catégorie" />
            <hr className="my-1" />
            <p className="text-xs font-semibold text-brand-orange">Bonus / Upsell en checkout</p>
            <select className="input !text-sm !py-1.5" defaultValue={course.upsell_course_id || ""} onChange={(e) => updateCourseField({ upsell_course_id: e.target.value || null })} data-testid="course-upsell-select">
              <option value="">— Aucun upsell —</option>
              {(allCourses || []).filter((c) => c.course_id !== course.course_id).map((c) => (
                <option key={c.course_id} value={c.course_id}>{c.title}</option>
              ))}
            </select>
            <input className="input !text-sm !py-1.5" type="number" step="0.01" min="0" max="100" defaultValue={course.upsell_discount_pct ?? ""} onBlur={(e) => updateCourseField({ upsell_discount_pct: e.target.value !== "" ? parseFloat(e.target.value) : null })} placeholder="Remise upsell (%)" data-testid="course-upsell-discount-input" />
          </div>
        )}

        <div className="space-y-3">
          {(course.modules || []).map((m) => (
            <div key={m.module_id} className="border border-emerald-100 rounded-lg p-2" data-testid={`admin-module-${m.module_id}`}>
              <div className="flex items-center justify-between mb-2">
                <input className="font-heading font-bold text-sm bg-transparent flex-1 px-1" defaultValue={m.title} onBlur={(e) => renameModule(m.module_id, e.target.value)} />
                <button onClick={() => removeModule(m.module_id)} className="text-red-400 hover:text-red-600 p-1"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
              <div className="space-y-1">
                {(m.lessons || []).map((l, lessonIndex) => (
                  <div key={l.lesson_id} className={`rounded px-2 py-1.5 text-xs ${activeLesson === l.lesson_id ? "bg-brand-green text-white" : "bg-emerald-50 hover:bg-emerald-100"}`}>
                    <div className="flex items-center justify-between gap-1">
                      <button onClick={() => openLesson(l.lesson_id)} className="flex-1 text-left truncate" data-testid={`admin-lesson-open-${l.lesson_id}`}>
                        {l.type === "assignment" ? "📝 " : l.type === "quiz" ? "🧠 " : "▶ "}{l.title}
                      </button>
                      <button
                        onClick={() => moveLessonWithinModule(m, lessonIndex, -1)}
                        disabled={lessonIndex === 0}
                        className="opacity-60 hover:opacity-100 disabled:opacity-20"
                        title="Monter"
                        data-testid={`admin-lesson-up-${l.lesson_id}`}
                      >
                        <ChevronUp className="w-3 h-3" />
                      </button>
                      <button
                        onClick={() => moveLessonWithinModule(m, lessonIndex, 1)}
                        disabled={lessonIndex === (m.lessons || []).length - 1}
                        className="opacity-60 hover:opacity-100 disabled:opacity-20"
                        title="Descendre"
                        data-testid={`admin-lesson-down-${l.lesson_id}`}
                      >
                        <ChevronDown className="w-3 h-3" />
                      </button>
                      <button onClick={() => duplicateLesson(l.lesson_id)} className="opacity-60 hover:opacity-100" title="Dupliquer"><Copy className="w-3 h-3" /></button>
                      <button onClick={() => removeLesson(l.lesson_id)} className="opacity-60 hover:opacity-100" title="Supprimer"><Trash2 className="w-3 h-3" /></button>
                    </div>
                    {(course.modules || []).length > 1 && (
                      <select
                        value={m.module_id}
                        onChange={(e) => moveLessonToModule(l.lesson_id, e.target.value)}
                        className={`mt-1 w-full rounded border px-1 py-1 text-[11px] ${activeLesson === l.lesson_id ? "bg-white text-gray-800 border-white/70" : "bg-white text-gray-700 border-emerald-100"}`}
                        title="Déplacer vers un autre module"
                        data-testid={`admin-lesson-module-select-${l.lesson_id}`}
                      >
                        {(course.modules || []).map((moduleOption) => (
                          <option key={moduleOption.module_id} value={moduleOption.module_id}>
                            Déplacer vers : {moduleOption.title}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                ))}
              </div>
              <div className="mt-2 flex flex-wrap gap-1">
                <button onClick={() => addLesson(m.module_id, "lesson")} className="text-xs px-2 py-1 rounded bg-emerald-100 hover:bg-emerald-200" data-testid={`admin-add-lesson-${m.module_id}`}>+ Leçon</button>
                <button onClick={() => addLesson(m.module_id, "assignment")} className="text-xs px-2 py-1 rounded bg-amber-100 hover:bg-amber-200">+ Devoir</button>
                <button onClick={() => addLesson(m.module_id, "quiz")} className="text-xs px-2 py-1 rounded bg-purple-100 hover:bg-purple-200">+ Quiz</button>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4 pt-3 border-t border-emerald-100">
          <input className="input !text-sm !py-1.5" placeholder="Nouveau module..." value={newModuleTitle} onChange={(e) => setNewModuleTitle(e.target.value)} data-testid="admin-new-module-input" />
          <button onClick={addModule} className="btn-primary w-full justify-center mt-2 !py-1.5 !text-sm" data-testid="admin-add-module-btn"><Plus className="w-3.5 h-3.5" /> Ajouter un module</button>
        </div>
      </aside>

      <main className="bg-brand-mint p-6 lg:p-10">
        {!activeLessonData ? (
          <div className="max-w-2xl mx-auto card p-10 text-center mt-12">
            <Pencil className="w-12 h-12 text-brand-green mx-auto mb-3" />
            <h2 className="font-heading text-2xl font-bold">Sélectionne une leçon</h2>
            <p className="text-sm text-gray-500 mt-2">Crée des modules, ajoute des leçons et édite leur contenu en blocs (texte, image, vidéo, YouTube...).</p>
          </div>
        ) : (
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
              <input
                className="input !text-2xl !font-heading !font-bold flex-1"
                value={activeLessonData.title}
                onChange={(e) => setActiveLessonData((d) => ({ ...d, title: e.target.value }))}
                data-testid="lesson-title-edit-input"
              />
              <select className="input !w-auto" value={activeLessonData.type} onChange={(e) => setActiveLessonData((d) => ({ ...d, type: e.target.value }))} data-testid="lesson-type-select">
                <option value="lesson">Leçon</option>
                <option value="assignment">Devoir</option>
                <option value="quiz">Quiz</option>
              </select>
              <button onClick={saveLesson} className="btn-primary" data-testid="lesson-save-btn"><Save className="w-4 h-4" /> Enregistrer</button>
              <button onClick={() => duplicateLesson(activeLesson)} className="btn-ghost" data-testid="lesson-duplicate-btn"><Copy className="w-4 h-4" /> Dupliquer</button>
            </div>
            {savingMsg && <p className="text-sm text-emerald-600 mb-4">{savingMsg}</p>}

            {activeLessonData.type === "assignment" && (() => {
              const settings = defaultAssignmentSettings(activeLessonData.assignment_settings);
              const rows = [
                { key: "enable_rating", label: "Activer la notation", help: "Permet de corriger le devoir avec une note en 5 étoiles, avec demi-étoiles possibles." },
                { key: "enable_private_discussion", label: "Activer la discussion privée", help: "Crée un fil privé entre l'apprenant et l'admin autour de ce devoir." },
                { key: "allow_multiple_files", label: "Accepter plusieurs documents", help: "Permet à l'apprenant de joindre plusieurs fichiers lors du rendu." },
                { key: "enable_round_trips", label: "Activer les allers-retours", help: "Permet à l'apprenant et à l'admin de continuer la conversation après correction." },
              ];
              return (
                <div className="card p-5 mb-6 space-y-4" data-testid="assignment-settings-panel">
                  <div>
                    <p className="label mb-1">Paramètres du devoir</p>
                    <h3 className="font-heading text-xl font-black">Correction avancée</h3>
                    <p className="text-sm text-gray-600 mt-1">Ces options ne s'appliquent qu'aux leçons de type devoir.</p>
                  </div>
                  <div className="space-y-3">
                    {rows.map((row) => (
                      <label key={row.key} className="flex items-start gap-3 rounded-2xl border border-emerald-100 bg-white p-4 cursor-pointer">
                        <input
                          type="checkbox"
                          className="mt-1 h-5 w-5 accent-brand-green"
                          checked={settings[row.key]}
                          onChange={(e) => updateAssignmentSettings({ [row.key]: e.target.checked })}
                          data-testid={`assignment-setting-${row.key}`}
                        />
                        <span className="flex-1">
                          <span className="font-semibold text-gray-900">{row.label}</span>
                          <span className="block text-xs text-gray-600 mt-1">{row.help}</span>
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })()}

            {activeLessonData.type === "quiz" && (() => {
              const quiz = normalizeQuiz(activeLessonData.quiz);
              return (
                <div className="card p-5 mb-6 space-y-6" data-testid="quiz-editor-panel">
                  <div>
                    <p className="label mb-2">Paramètres du quiz</p>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <label className="block">
                        <span className="text-sm font-semibold text-gray-700">Taux minimum de réussite (%)</span>
                        <input
                          className="input mt-1"
                          type="number"
                          min="0"
                          max="100"
                          value={quiz.passingScore}
                          onChange={(e) => updateQuiz({ passingScore: Math.max(0, Math.min(100, Number(e.target.value) || 0)) })}
                          data-testid="quiz-passing-score-input"
                        />
                      </label>
                      <label className="flex items-center gap-3 rounded-xl border border-emerald-100 p-3 bg-emerald-50/50">
                        <input
                          type="checkbox"
                          checked={quiz.pagination}
                          onChange={(e) => updateQuiz({ pagination: e.target.checked })}
                          className="h-5 w-5 accent-brand-green"
                          data-testid="quiz-pagination-checkbox"
                        />
                        <span className="text-sm font-semibold">Activer la pagination — 1 question par page</span>
                      </label>
                    </div>
                  </div>

                  <div className="space-y-4">
                    {quiz.questions.map((question, questionIndex) => (
                      <div key={question.id} className="rounded-2xl border-2 border-dashed border-teal-300 bg-white overflow-hidden" data-testid={`quiz-question-${questionIndex}`}>
                        <div className="bg-teal-400 text-white px-4 py-3 flex items-center justify-between">
                          <p className="font-heading font-bold">Question #{questionIndex + 1}</p>
                          <button type="button" onClick={() => removeQuizQuestion(questionIndex)} className="text-white/90 hover:text-white text-sm" title="Supprimer la question">Supprimer</button>
                        </div>
                        <div className="p-4 space-y-4">
                          <input
                            className="input"
                            value={question.text}
                            onChange={(e) => updateQuizQuestion(questionIndex, { text: e.target.value })}
                            placeholder="Entrez une question"
                            data-testid={`quiz-question-text-${questionIndex}`}
                          />
                          <select
                            className="input max-w-xs"
                            value={question.type}
                            onChange={(e) => updateQuizQuestion(questionIndex, { type: e.target.value })}
                            data-testid={`quiz-question-type-${questionIndex}`}
                          >
                            <option value="multiple">Choix multiple / QCM</option>
                            <option value="single">Question à choix unique</option>
                          </select>

                          <div className="space-y-3">
                            {question.answers.map((answer, answerIndex) => (
                              <div key={answer.id} className="grid grid-cols-[32px_1fr_auto] gap-3 items-center border-t border-gray-100 pt-3">
                                <input
                                  type={question.type === "multiple" ? "checkbox" : "radio"}
                                  name={`correct-answer-${question.id}`}
                                  checked={answer.isCorrect}
                                  onChange={(e) => updateQuizAnswer(questionIndex, answerIndex, { isCorrect: question.type === "multiple" ? e.target.checked : true })}
                                  className="h-5 w-5 accent-teal-400"
                                  title="Définir comme bonne réponse"
                                  data-testid={`quiz-answer-correct-${questionIndex}-${answerIndex}`}
                                />
                                <input
                                  className="input"
                                  value={answer.text}
                                  onChange={(e) => updateQuizAnswer(questionIndex, answerIndex, { text: e.target.value })}
                                  placeholder="Entrez une réponse"
                                  data-testid={`quiz-answer-text-${questionIndex}-${answerIndex}`}
                                />
                                <button
                                  type="button"
                                  onClick={() => removeQuizAnswer(questionIndex, answerIndex)}
                                  disabled={question.answers.length <= 2}
                                  className="p-2 text-red-500 hover:bg-red-50 rounded-lg disabled:opacity-30"
                                  title="Supprimer la réponse"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            ))}
                          </div>

                          <button type="button" onClick={() => addQuizAnswer(questionIndex)} className="btn-ghost !px-4 !py-2 text-sm">+ Ajouter une réponse</button>
                        </div>
                      </div>
                    ))}
                    <button type="button" onClick={addQuizQuestion} className="btn-ghost mx-auto flex">+ Ajouter une question</button>
                  </div>
                </div>
              );
            })()}

            <div className="space-y-3">
              {(activeLessonData.blocks || []).map((b, i) => (
                <BlockEditor
                  key={b.id}
                  block={b}
                  onChange={(blk) => updateBlock(i, blk)}
                  onRemove={() => removeBlock(i)}
                  onMove={(dir) => moveBlock(i, dir)}
                  isFirst={i === 0}
                  isLast={i === (activeLessonData.blocks?.length || 0) - 1}
                />
              ))}
            </div>

            <div className="mt-6 card p-4">
              <p className="label mb-2">Ajouter un bloc</p>
              <div className="flex flex-wrap gap-2">
                {BLOCK_TYPES.map((b) => (
                  <button key={b.type} onClick={() => addBlock(b.type)} className="btn-ghost !px-3 !py-1.5 text-sm" data-testid={`add-block-${b.type}`}>+ {b.label}</button>
                ))}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
